import { useEffect, useRef, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { HyggeLogo } from '@/components/icons/hygge-logo';
import { useAuthStore } from '@/stores/auth';
import { useExtensionsStore } from '@/stores/extensions';
import { AUTH_EVENTS } from '@/lib/api';

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, isInitialized, initialize, logout } = useAuthStore();
  const loadExtensions = useExtensionsStore((s) => s.load);
  const hasInitialized = useRef(false);
  const hasRedirected = useRef(false);

  // Initialize auth on mount
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    initialize();
  }, [initialize]);

  // Load extensions store after authentication
  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      loadExtensions();
    }
  }, [isInitialized, isAuthenticated, loadExtensions]);

  // Listen for session expired events from API interceptor
  useEffect(() => {
    const handleSessionExpired = async () => {
      // Clear auth state
      await logout();
      // Navigate to login
      navigate({ to: '/login', replace: true });
    };

    window.addEventListener(AUTH_EVENTS.SESSION_EXPIRED, handleSessionExpired);

    return () => {
      window.removeEventListener(AUTH_EVENTS.SESSION_EXPIRED, handleSessionExpired);
    };
  }, [logout, navigate]);

  // Redirect to login if not authenticated
  useEffect(() => {
    // Prevent multiple redirects
    if (hasRedirected.current) return;

    if (isInitialized && !isLoading && !isAuthenticated) {
      hasRedirected.current = true;
      navigate({ to: '/login', replace: true });
    }
  }, [isAuthenticated, isLoading, isInitialized, navigate]);

  // Show loading while checking auth
  if (!isInitialized || isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background">
        <HyggeLogo className="h-10 opacity-80" />
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // If not authenticated after loading, show loading (redirect in progress)
  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background">
        <HyggeLogo className="h-10 opacity-80" />
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <>{children}</>;
}
